import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest, API_URL } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface JiraInstallation {
  id: string;
  siteUrl: string;
  siteName: string | null;
  cloudId: string; // Kept for internal use
  organizationId: string;
  isActive: boolean;
  suspendedAt: string | null;
  installedAt: string;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface JiraStatus {
  id: string;
  name: string;
  statusCategory: {
    key: string;
    name: string;
  };
}

interface JiraState {
  currentInstallation: JiraInstallation | null;
  installations: JiraInstallation[];
  projects: JiraProject[];
  statuses: JiraStatus[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  statusesLoading: boolean; // Separate loading state for status fetching
  error: string;
}

const initialState: JiraState = {
  currentInstallation: null,
  installations: [],
  projects: [],
  statuses: [],
  status: 'idle',
  statusesLoading: false,
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getInstallations = createAsyncThunk(
  'jira/getInstallations',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/jira/installations?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getInstallation = createAsyncThunk(
  'jira/getInstallation',
  async (installationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/jira/installations/${installationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const disconnectJira = createAsyncThunk(
  'jira/disconnectJira',
  async (installationId: string, { rejectWithValue }) => {
    try {
      await apiRequest(`/jira/installations/${installationId}`, { method: 'DELETE' });
      return installationId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getJiraProjects = createAsyncThunk(
  'jira/getJiraProjects',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/jira/oauth/projects?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getJiraStatuses = createAsyncThunk(
  'jira/getJiraStatuses',
  async ({ projectKey, organizationId }: { projectKey: string; organizationId: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/jira/oauth/projects/${projectKey}/statuses?organizationId=${organizationId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get installations
  [getInstallations.pending.type]: { status: 'loading' as const },
  [getInstallations.fulfilled.type]: (state: JiraState, action: any) => {
    // Handle null response (404 - no installations found)
    if (!action.payload) {
      state.installations = [];
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend returns array directly in response: { response: [...], metadata: {...} }
    // Backend already filters by isActive: true
    const installations = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.installations || action.payload.installations || [];

    state.installations = installations;
    state.currentInstallation = installations[0] || null;
    state.status = 'success';
    state.error = '';
  },
  [getInstallations.rejected.type]: (state: JiraState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installations';
  },

  // Get single installation
  [getInstallation.pending.type]: { status: 'loading' as const },
  [getInstallation.fulfilled.type]: (state: JiraState, action: any) => {
    // Handle null response (404 - installation not found)
    if (!action.payload) {
      state.currentInstallation = null;
      state.status = 'success';
      state.error = '';
      return;
    }
    // Backend wraps response in { response: {...}, metadata: {...} }
    state.currentInstallation = action.payload.response || action.payload;
    state.status = 'success';
    state.error = '';
  },
  [getInstallation.rejected.type]: (state: JiraState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch installation';
  },

  // Disconnect Jira
  [disconnectJira.pending.type]: { status: 'loading' as const },
  [disconnectJira.fulfilled.type]: (state: JiraState) => {
    state.currentInstallation = null;
    state.installations = [];
    state.status = 'success';
    state.error = '';
  },
  [disconnectJira.rejected.type]: (state: JiraState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to disconnect Jira';
  },

  // Get Jira projects
  [getJiraProjects.pending.type]: { status: 'loading' as const },
  [getJiraProjects.fulfilled.type]: (state: JiraState, action: any) => {
    if (!action.payload) {
      state.projects = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    const projects = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.projects || action.payload.projects || [];
    state.projects = projects;
    state.status = 'success';
    state.error = '';
  },
  [getJiraProjects.rejected.type]: (state: JiraState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch Jira projects';
  },

  // Get Jira statuses - uses separate statusesLoading to avoid full-tab reload
  [getJiraStatuses.pending.type]: (state: JiraState) => {
    state.statusesLoading = true;
  },
  [getJiraStatuses.fulfilled.type]: (state: JiraState, action: any) => {
    state.statusesLoading = false;
    if (!action.payload) {
      state.statuses = [];
      state.error = '';
      return;
    }
    const statuses = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.statuses || action.payload.statuses || [];
    state.statuses = statuses;
    state.error = '';
  },
  [getJiraStatuses.rejected.type]: (state: JiraState, action: any) => {
    state.statusesLoading = false;
    state.error = action.payload || 'Failed to fetch Jira statuses';
  },
};

// ==============================|| SLICE ||============================== //

export const jiraSlice = createSlice({
  name: 'jira',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentInstallation(state, action) {
      state.currentInstallation = action.payload;
    },
    clearJiraState(state) {
      state.currentInstallation = null;
      state.installations = [];
      state.projects = [];
      state.statuses = [];
      state.status = 'idle';
      state.statusesLoading = false;
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentInstallation, clearJiraState } = jiraSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectJiraInstallations = (state: any) => state.jira.installations;
export const selectCurrentInstallation = (state: any) => state.jira.currentInstallation;
export const selectJiraStatus = (state: any) => state.jira.status;
export const selectJiraStatusesLoading = (state: any) => state.jira.statusesLoading;
export const selectJiraError = (state: any) => state.jira.error;
export const selectJiraProjects = (state: any) => state.jira.projects;
export const selectJiraStatuses = (state: any) => state.jira.statuses;

// ==============================|| HELPER ||============================== //

export const initiateJiraConnect = createAsyncThunk(
  'jira/initiateConnect',
  async (organizationId: string, { rejectWithValue }) => {
    try {
      // Call API with authentication to get Jira installation URL (same as GitHub/GitLab)
      const response = await apiRequest(`/jira/install?organizationId=${organizationId}`);
      const installUrl = response.response?.installUrl || response.installUrl;

      if (!installUrl) {
        throw new Error('No installation URL returned from API');
      }

      // Redirect browser to Atlassian OAuth
      window.location.href = installUrl;

      return { installUrl };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export default jiraSlice.reducer;
